/**
 * ============================================================================
 * LINKS PAGE - 100% Ready
 * ============================================================================
 * 
 * Page for managing all transaction links
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserDashboardLayout } from '../components/user';
import { useTransaction } from '../hooks/useTransaction';
import { useToast } from '../providers';
import { useI18n } from '../providers';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Tabs from '../components/common/Tabs';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Spinner from '../components/common/Spinner';
import EmptyState from '../components/common/EmptyState';
import StatusBadge from '../components/common/StatusBadge';
import { useCopyToClipboard } from '../hooks/useCopyToClipboard';

/**
 * Links Page Component
 * @returns {JSX.Element}
 */
const Links = () => {
  const navigate = useNavigate();
  const { listTransactions, transactions, loading } = useTransaction();
  const { showError, showSuccess } = useToast();
  const { formatCurrency, formatDate, language } = useI18n();
  const { copy, copied } = useCopyToClipboard();

  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        await listTransactions({
          status: activeFilter === 'all' ? undefined : activeFilter,
          search: searchQuery || undefined,
        });
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement des transactions' : 'Failed to load transactions')
        );
        console.error('Load transactions error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [activeFilter, searchQuery, listTransactions, showError, language]);

  const handleCopyLink = async (link) => {
    try {
      await copy(link);
      showSuccess(language === 'fr' ? 'Lien copié!' : 'Link copied!');
    } catch (error) {
      showError(language === 'fr' ? 'Échec de la copie' : 'Failed to copy');
    }
  };

  const getStatusVariant = (status) => {
    const variants = {
      created: 'default',
      link_sent: 'info',
      payment_pending: 'warning',
      escrow_funded: 'success',
      in_progress: 'info',
      delivered: 'success',
      completed: 'success',
      disputed: 'error',
      cancelled: 'default',
    };
    return variants[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      created: '📝',
      link_sent: '📤',
      payment_pending: '⏳',
      escrow_funded: '🔒',
      in_progress: '⚙️',
      delivered: '📦',
      completed: '✅',
      disputed: '⚠️',
      cancelled: '❌',
    };
    return icons[status] || '📄';
  };

  const tabs = [
    { id: 'all', label: language === 'fr' ? 'Tout' : 'All', count: transactions?.length || 0 },
    {
      id: 'link_sent',
      label: language === 'fr' ? 'Liens envoyés' : 'Links Sent',
      count: transactions?.filter((t) => t.status === 'link_sent').length || 0,
    },
    {
      id: 'created',
      label: language === 'fr' ? 'Sans lien' : 'No Link',
      count: transactions?.filter((t) => t.status === 'created').length || 0,
    },
  ];

  const filteredTransactions =
    activeFilter === 'all'
      ? transactions || []
      : (transactions || []).filter((t) => t.status === activeFilter);

  if (isLoading || loading) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spinner size="lg" />
        </div>
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      {/* Header */}
      <div className="mb-8 animate-fadeInUp">
        <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-2">
          🔗 {language === 'fr' ? 'Mes Liens' : 'My Links'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {language === 'fr'
            ? 'Gérez et partagez les liens de vos transactions'
            : 'Manage and share your transaction links'}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
        <Card variant="glass" className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-blue-200 dark:border-blue-800">
          <div className="text-center">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
              {language === 'fr' ? 'Total' : 'Total'}
            </p>
            <p className="text-3xl font-black text-blue-600 dark:text-blue-400">
              {transactions?.length || 0}
            </p>
          </div>
        </Card>
        <Card variant="glass" className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
          <div className="text-center">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
              {language === 'fr' ? 'Liens envoyés' : 'Links Sent'}
            </p>
            <p className="text-3xl font-black text-green-600 dark:text-green-400">
              {transactions?.filter((t) => t.status === 'link_sent' || t.shortLink).length || 0}
            </p>
          </div>
        </Card>
        <Card variant="glass" className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800">
          <div className="text-center">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
              {language === 'fr' ? 'Sans lien' : 'No Link'}
            </p>
            <p className="text-3xl font-black text-purple-600 dark:text-purple-400">
              {transactions?.filter((t) => !t.shortLink && t.status === 'created').length || 0}
            </p>
          </div>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder={language === 'fr' ? 'Rechercher une transaction...' : 'Search transactions...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
        <Tabs tabs={tabs} activeTab={activeFilter} onTabChange={setActiveFilter} variant="pills" />
      </Card>

      {/* Transactions List */}
      {filteredTransactions.length > 0 ? (
        <div className="space-y-4">
          {filteredTransactions.map((transaction, index) => (
            <Card
              key={transaction.id}
              variant="glass"
              hover
              className="animate-fadeInUp"
              style={{ animationDelay: `${0.3 + index * 0.05}s` }}
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${
                    transaction.status === 'link_sent' || transaction.shortLink
                      ? 'from-green-500 to-emerald-500'
                      : 'from-yellow-500 to-orange-500'
                  } flex items-center justify-center text-2xl shadow-lg flex-shrink-0`}
                >
                  {getStatusIcon(transaction.status)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-black text-gray-900 dark:text-white truncate">
                      {transaction.title}
                    </h3>
                    <StatusBadge variant={getStatusVariant(transaction.status)} size="sm">
                      {transaction.status?.toUpperCase()}
                    </StatusBadge>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                    <span>
                      {language === 'fr' ? 'Montant' : 'Amount'}:{' '}
                      {formatCurrency(transaction.amount || 0, transaction.currency || 'XAF')}
                    </span>
                    <span>•</span>
                    <span>
                      {language === 'fr' ? 'Créé le' : 'Created'}:{' '}
                      {transaction.createdAt && formatDate(transaction.createdAt)}
                    </span>
                    {transaction.shortLink && (
                      <>
                        <span>•</span>
                        <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                          <span>🔗</span>
                          {language === 'fr' ? 'Lien généré' : 'Link generated'}
                        </span>
                      </>
                    )}
                  </div>
                  {transaction.shortLink && (
                    <div className="mt-3 flex items-center gap-2">
                      <code className="flex-1 text-xs font-mono text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded truncate">
                        {transaction.shortLink}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyLink(transaction.shortLink)}
                        className="flex-shrink-0"
                      >
                        {copied ? '✓' : '📋'}
                      </Button>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {transaction.shortLink ? (
                    <Button
                      variant="gradient"
                      size="sm"
                      onClick={() => navigate(`/links/${transaction.id}`)}
                      className="bg-gradient-to-r from-blue-600 to-green-600 shadow-xl hover:shadow-2xl transition-all"
                    >
                      {language === 'fr' ? 'Gérer' : 'Manage'} →
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/links/${transaction.id}`)}
                      className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 hover:from-blue-100 hover:to-green-100 dark:hover:from-blue-900/30 dark:hover:to-green-900/30"
                    >
                      {language === 'fr' ? 'Créer un lien' : 'Create Link'} →
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card variant="glass" className="p-12">
          <EmptyState
            icon="🔗"
            title={language === 'fr' ? 'Aucun lien trouvé' : 'No links found'}
            description={
              language === 'fr'
                ? 'Vous n\'avez pas encore de liens de transaction'
                : "You don't have any transaction links yet"
            }
          />
        </Card>
      )}
    </UserDashboardLayout>
  );
};

export default Links;
